import * as React from 'react'
import { OptionsProps, isSubRowSelected } from './useSelectAdvance'
import { Checkbox } from '../Checkbox'
import { CircularProgress } from '../CircularProgress'
import { Row, Table } from '@tanstack/react-table'
import { VirtualItem } from '@tanstack/react-virtual'
import { FormControlProps } from '../FormControl'
import { ListItemButton, ListItemButtonBaseProps } from '../ListItemButton'
import { TouchRippleApiProps } from '../ButtonBase'
import { IconButton } from '../IconButton'
import ChevronDownIcon from '../internal/svg-icons/ChevronDown'
import ChevronRightIcon from '../internal/svg-icons/ChevronRight'
import { Skeleton } from '../Skeleton'
import { insertNestedData } from './utils'
import styles from './index.module.scss'
import { Box } from '../Box'
import clsx from 'clsx'

export function getAbortController(): AbortController | undefined {
  if (typeof AbortController === 'function') {
    return new AbortController()
  }
  return
}

export interface RenderListOptionItemProps<T> extends OptionsProps {
  row: Row<T>
  renderOption?: ({
    row,
    isSelected,
    disableRowExpansion,
    disableCheckbox,
  }: {
    row: Row<T>
    isSelected: boolean | 'some' | 'all'
    disableRowExpansion: boolean
    disableCheckbox?: boolean
  }) => JSX.Element
  style: Record<string, any>
  virtualItem: VirtualItem
  measureElement: (node: any | null) => void
  size: FormControlProps['size']
  table: Table<T>
  ListItemButtonProps?: ListItemButtonBaseProps
  currentRowLoading?: boolean
  onFetchSubRows?: (args: {
    row: Row<T>
    insertNestedData: <T extends Record<string, any>>({
      existingRows,
      subRowsToInsert,
      path,
      getRowId,
      subRowKey,
    }: {
      existingRows: T[]
      subRowsToInsert: T[]
      path: string[]
      getRowId: (row: T) => string
      subRowKey: string
    }) => T[]
  }) => unknown
}

export interface RenderListOptionItemSkeletonProps {
  style?: Record<string, any>
  virtualItem: VirtualItem
  measureElement: (node: any | null) => void
  size: FormControlProps['size']
}

export const DefaultListOptionItemWrapper = <T,>({
  disableRowExpansion = false,
  disableCheckbox = false,
  /**
   * selectionStyle takes three values
   * @default none
   * - default: will highlight parent when any of the children is selected
   * - checkbox: will highlight parent only when all children underneath are selected
   * - none: will not highlight parent only the selected nodes
   */
  selectionStyle = 'none',
  arrowPosition = 'left',
}: Partial<{
  disableRowExpansion?: boolean
  disableCheckbox?: boolean
  selectionStyle: 'partial' | 'none' | 'all'
  arrowPosition?: 'left' | 'right'
}> = {}) =>
  function DefaultListOptionItem(props: RenderListOptionItemProps<T>) {
    const {
      row,
      renderOption,
      isDisabled,
      label: labelProp,
      style,
      virtualItem,
      measureElement,
      size,
      table,
      currentRowLoading,
      ListItemButtonProps,
      onFetchSubRows,
      ...others
    } = props

    let label = labelProp

    const { rowSelection } = table.getState()

    const isSelected =
      row.getCanExpand() === false || selectionStyle === 'none'
        ? row.getIsSelected()
        : isSubRowSelected(row, rowSelection, table)

    const expandHandler = row.getToggleExpandedHandler()
    const touchRippleRef = React.useRef<TouchRippleApiProps>(null)
    const depth = row.depth

    if (renderOption) {
      label = renderOption({
        row,
        isSelected,
        disableRowExpansion,
        disableCheckbox,
      })
    }
    const CircularProgressSize = {
      small: 18,
      medium: 21,
      large: 24,
    } as const
    return (
      // @ts-ignore
      <ListItemButton
        Component="li"
        {...ListItemButtonProps}
        {...others}
        style={style}
        classes={{
          root: styles.listItemRoot,
          focusVisible: styles.listItemFocusVisible,
          selected: styles.listItemSelected,
        }}
        data-index={virtualItem.index}
        ref={measureElement}
        selected={
          selectionStyle === 'all'
            ? isSelected === 'some'
              ? false
              : Boolean(isSelected)
            : Boolean(isSelected)
        }
        disabled={isDisabled}
        touchRippleRef={touchRippleRef}
        size={size}
      >
        <Box
          className={clsx(
            styles.listItemBox,
            arrowPosition === 'left'
              ? styles['arrowPosition--left']
              : styles['arrowPosition--right']
          )}
          style={{ paddingLeft: depth * 12 }}
        >
          <Box className={styles.listItemLabel}>
            {!disableCheckbox ? (
              <Checkbox
                indeterminate={isSelected === 'some'}
                checked={isSelected === 'all' || isSelected === true}
                size={size}
                sx={{ flexShrink: 0 }}
              />
            ) : null}
            {label}
          </Box>
          {!disableRowExpansion ? (
            <Box
              className={styles.listItemIconButton}
              sx={{
                ...(!row.getCanExpand()
                  ? arrowPosition === 'left'
                    ? { visibility: 'hidden' }
                    : { display: 'none' }
                  : ''),
              }}
            >
              <IconButton
                size={size}
                onClick={e => {
                  touchRippleRef.current?.stop(e, () => null)
                  e.preventDefault()
                  e.stopPropagation()
                  if (!row.getIsExpanded() && row.subRows.length === 0) {
                    if (typeof onFetchSubRows === 'function') {
                      onFetchSubRows({
                        row,
                        insertNestedData,
                      })
                    }
                  }
                  expandHandler()
                }}
                color="primary"
                disabled={currentRowLoading}
              >
                {currentRowLoading ? (
                  <CircularProgress
                    //@ts-ignore
                    size={CircularProgressSize[size]}
                    thickness={4}
                  />
                ) : row.getIsExpanded() ? (
                  <ChevronDownIcon />
                ) : (
                  <ChevronRightIcon />
                )}
              </IconButton>
            </Box>
          ) : null}
        </Box>
      </ListItemButton>
    )
  }

export const DefaultSkeletonListOptionItem = (
  props: RenderListOptionItemSkeletonProps
) => {
  const { style, virtualItem, measureElement } = props
  return (
    <ListItemButton
      style={style}
      data-index={virtualItem.index}
      ref={measureElement}
      tabIndex={-1}
    >
      <Skeleton variant="text" animation={false} sx={{ width: 100 }} />
    </ListItemButton>
  )
}
